<template>
  <nav class="navigation">
    <ul class="nav-list">
      <li v-for="item in availableNavigationItems" :key="item.path" class="nav-item">
        <el-dropdown v-if="item.children?.length" trigger="hover" placement="bottom">
          <span
            class="nav-link"
            :class="{ active: isGroupActive(item) }"
            :title="item.name"
            tabindex="0"
          >
            <span v-if="item.icon" class="nav-icon">{{ item.icon }}</span>
            <span class="nav-text">{{ item.name }}</span>
            <span class="nav-caret">▾</span>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="child in item.children"
                :key="child.path"
                @click="router.push(child.path)"
              >
                <span v-if="child.icon" class="dropdown-icon">{{ child.icon }}</span>
                {{ child.name }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <router-link
          v-else
          :to="item.path"
          class="nav-link"
          :class="{ active: $route.path === item.path }"
          :title="item.name"
        >
          <span v-if="item.icon" class="nav-icon">{{ item.icon }}</span>
          <span class="nav-text">{{ item.name }}</span>
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { ElDropdown, ElDropdownMenu, ElDropdownItem } from 'element-plus'
import { useNavigation } from '@/composables/useNavigation'
import type { NavigationItem } from '@/types/auth'

const { availableNavigationItems } = useNavigation()
const route = useRoute()
const router = useRouter()

const isGroupActive = (item: NavigationItem) => {
  return item.children?.some((child) => route.path === child.path) ?? false
}
</script>

<style scoped>
.navigation {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
}

.nav-list {
  display: flex;
  align-items: center;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  position: relative;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  color: #64748b;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  font-size: 0.9rem;
  white-space: nowrap;
  cursor: pointer;
  outline: none;
}

.nav-caret {
  font-size: 0.7rem;
  opacity: 0.7;
}

.dropdown-icon {
  margin-right: 8px;
}

.nav-link:hover {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
}

.nav-link.active {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.12);
}

.nav-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.nav-text {
  transition: opacity 0.2s ease;
}

/* Мобильная версия - только иконки */
@media (max-width: 768px) {
  .nav-list {
    gap: 1px;
    justify-content: center;
  }

  .nav-link {
    padding: 4px 6px;
    gap: 0;
    min-width: 36px;
    justify-content: center;
  }

  .nav-text,
  .nav-caret {
    display: none;
  }

  .nav-icon {
    font-size: 1rem;
  }
}

/* Очень маленькие экраны */
@media (max-width: 480px) {
  .nav-list {
    gap: 0px;
    justify-content: space-between;
    width: 100%;
  }

  .nav-link {
    padding: 2px 3px;
    min-width: 28px;
    flex: 1;
    max-width: 40px;
  }

  .nav-icon {
    font-size: 0.85rem;
  }
}

/* Планшеты */
@media (min-width: 769px) and (max-width: 1024px) {
  .nav-link {
    padding: 8px 10px;
    font-size: 0.85rem;
  }

  .nav-text {
    font-size: 0.85rem;
  }
}
</style>
